import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './Filters.css';
import { AuthContext } from '../../App';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

function Filters({ onFilterChange, resetFilters }) {
    const [selectedCompany, setSelectedCompany] = useState('Company');
    const [selectedPosition, setSelectedPosition] = useState('Position');
    const [selectedCategory, setSelectedCategory] = useState('Category');
    const [selectedStatus, setSelectedStatus] = useState('Status');
    const [companies, setCompanies] = useState([]);
    const [positions, setPositions] = useState([]);
    const [categories, setCategories] = useState([]);
    const { isSignedIn, isPremiumUser } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchInitialFilterOptions = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/filters`);
                const data = await response.json();
                setCompanies(data.companies);
                setCategories(data.categories);
                if (!selectedCompany || selectedCompany === 'Company') {
                    setPositions(data.positions);
                }
            } catch (error) {
                console.error('Error fetching initial filter options:', error);
            }
        };
        fetchInitialFilterOptions();
    }, [selectedCompany]);

    useEffect(() => {
        if (selectedCompany && selectedCompany !== 'Company') {
            const fetchPositions = async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/filters?company=${selectedCompany}`);
                    const data = await response.json();
                    setPositions(data.positions);
                } catch (error) {
                    console.error('Error fetching positions:', error);
                }
            };
            fetchPositions();
        } else {
            setPositions([]);
        }
    }, [selectedCompany]);

    const handleFilterChange = (filterType, value) => {
        onFilterChange({ [filterType]: value !== filterType ? value : undefined });
    };

    const renderCompanyOption = (company) => {
        // Debugging the value directly inside the function
        return isPremiumUser ? company : `🔒 ${company}`;
    };

    const handleCompanySelection = (e) => {
        const companyWithIcon = e.target.value;
        // Extract the company name, ignoring the lock/unlock icon
        const company = companyWithIcon.replace(/🔒 /, ''); // Only remove the lock icon

        if (!isPremiumUser) {
            // Use toast notification instead of alert
            const message = (
                <div>
                    This filter is available for premium users only. Try{' '}
                    <span
                        style={{ cursor: 'pointer', color: '#FFA115', fontWeight: 'bold' }}
                        onClick={() => navigate('/premium')} // Navigate to the premium page onClick
                    >
                        Premium
                    </span>{' '}
                    now.
                </div>
            );

            // Display the toast notification with the clickable link
            toast(message, {
                autoClose: 10000, // Adjust the auto-close time as needed
                closeOnClick: false, // Prevent toast from closing when clicking the content
            });

            return; // Prevent further execution
        } else {
            setSelectedCompany(company);
            handleFilterChange('company', company);
        }
    };

    const handleStatusChange = (e) => {
        const status = e.target.value;
        setSelectedStatus(status);
        if ((status === 'Todo' || status === 'Solved') && !isSignedIn) {
            navigate('/signin');
        } else {
            handleFilterChange('status', status);
        }
    };

    return (
        <div className="filters-container">
            <div className="filters-group">
                <button onClick={() => resetFilters()} className="filter-reset-button">All Questions</button>
                <select
                    name="company"
                    className="filter-dropdown"
                    value={selectedCompany}
                    onChange={handleCompanySelection}
                >
                    <option value="">Company</option>
                    {companies.map(company => (
                        <option key={company} value={company}>
                            {renderCompanyOption(company)}
                        </option>
                    ))}
                </select>

                {/* Position Filter */}
                <select
                    name="position"
                    className="filter-dropdown"
                    value={selectedPosition}
                    onChange={(e) => {
                        setSelectedPosition(e.target.value);
                        handleFilterChange('position', e.target.value);
                    }}
                >
                    <option value="">Position</option>
                    {positions.map(position => (
                        <option key={position} value={position}>{position}</option>
                    ))}
                </select>

                {/* Category Filter */}
                <select
                    name="category"
                    className="filter-dropdown"
                    value={selectedCategory}
                    onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        handleFilterChange('category', e.target.value);
                    }}
                >
                    <option value="">Category</option>
                    {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                    ))}
                </select>

                {/* Status Filter */}
                <select
                    name="status"
                    className="filter-dropdown"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                >
                    <option value="">Status</option>
                    <option value="Todo">Todo</option>
                    <option value="Solved">Solved</option>
                </select>
            </div>
            <div className="notification-text">
                <FontAwesomeIcon icon={faBell} /> Our platform provides over 15,000 questions. For a better user experience, you can use the filters provided above.
            </div>
            <ToastContainer position="top-center" autoClose={10000} />
        </div>
    );
}
export default Filters;